// src/context/AuthContext.js
import { createContext, useState, useEffect } from 'react';
import { toast } from 'react-toastify';

// ---------------------------------------------------------------------------
// localStorage usage policy — ISO 27001 A.8.3 / SOC 2 CC6.1
// localStorage is readable by any JS on the page (XSS risk).
// Rule: only non-sensitive display values (name, email) are persisted.
// Never store: phone numbers, loginId, isAdmin flag, tokens, or passwords.
// The JWT lives in an HttpOnly cookie managed entirely by the browser.
// ---------------------------------------------------------------------------

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [role, setRole] = useState('Trader');
  // phone is kept in React state only — never written to localStorage
  const [phone, setPhone] = useState('');
  const [fullName, setFullName] = useState('');
  const [firstName, setFirstName] = useState(localStorage.getItem('firstName') || '');
  const [lastName, setLastName] = useState(localStorage.getItem('lastName') || '');
  const [username, setUsername] = useState(localStorage.getItem('username') || '');
  const [email, setEmail] = useState(localStorage.getItem('email') || '');
  const [firmName, setFirmName] = useState(localStorage.getItem('firmName') || '');
  // isAdmin is kept in React state only — never persisted (prevents privilege escalation via localStorage tampering)
  const [isAdmin, setIsAdmin] = useState(false);
  const [accessCode, setAccessCode] = useState('');
  // authorizedFirms: kept in React state only — never written to localStorage.
  // Populated from the login response. Used by TraderDashboard for firm filter.
  // ISO 27001 A.8.3: firm access list must not be persisted client-side.
  const [authorizedFirms, setAuthorizedFirms] = useState([]);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSigningUp, setIsSigningUp] = useState(false);

  // registeredUser holds only display-safe values; loginId is not persisted to localStorage
  const [registeredUser, setRegisteredUser] = useState(
    (() => {
      try {
        const stored = JSON.parse(localStorage.getItem('registeredUser'));
        // Strip loginId if it was previously stored — do not re-persist it
        if (stored) return { username: stored.username };
        return null;
      } catch { return null; }
    })()
  );
  const [custodians, setCustodians] = useState([{ name: '', username: '', password: '' }]);
  const [code, setCode] = useState('');
  const [maskedPhone, setMaskedPhone] = useState('');
  const [error, setError] = useState('');

  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(null);

  useEffect(() => {
    setFullName(`${firstName} ${lastName}`.trim());
  }, [firstName, lastName]);




  const logout = () => {
    setRegisteredUser(null);
    setUsername('');
    setPassword('');
    setConfirmPassword('');
    setFullName('');
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setFirmName('');
    setMaskedPhone('');
    setIsAdmin(false);
    setAuthorizedFirms([]);
    setError('');
    // Clear all localStorage on logout (ISO 27001 A.8.3)
    localStorage.clear();
    toast.success('Logged out successfully!');
  };

  // Persist only non-sensitive display values to localStorage.
  // phone, loginId, isAdmin, and tokens are intentionally excluded. (ISO 27001 A.8.3)
  useEffect(() => {
    localStorage.setItem('firstName', firstName);
    localStorage.setItem('lastName', lastName);
    localStorage.setItem('username', username);
    localStorage.setItem('email', email);
    localStorage.setItem('firmName', firmName);
    // Store only display-safe subset of registeredUser — no loginId, no isAdmin
    if (registeredUser) {
      localStorage.setItem('registeredUser', JSON.stringify({ username: registeredUser.username }));
    } else {
      localStorage.removeItem('registeredUser');
    }
  }, [firstName, lastName, username, email, firmName, registeredUser]);

  return (
    <AuthContext.Provider value={{
      role, setRole,
      phone, setPhone,
      fullName, setFullName,
      firstName,setFirstName,
      lastName,setLastName,
      username, setUsername,
      email, setEmail,
      firmName, setFirmName,
      password, setPassword,
      confirmPassword, setConfirmPassword,
      isSigningUp, setIsSigningUp,
      registeredUser, setRegisteredUser,
      custodians, setCustodians,
      code, setCode,
      maskedPhone, setMaskedPhone,
      error, setError,
      clients, setClients,
      selectedClientId, setSelectedClientId,
      isAdmin, setIsAdmin,
      accessCode, setAccessCode,
      authorizedFirms, setAuthorizedFirms,
      logout
    }}>
      {children}
    </AuthContext.Provider>
  );
};
